import type { PropsWithChildren } from 'react';
import { createContext, use } from 'react';

export interface Environment {
	endpoints: {
		ENDPOINT_REST_API: string;
		ENDPOINT_CORAL: string;
		ENDPOINT_RADIO: string;
		ENDPOINT_GRAPHQL: string;
	};
	flags: {
		IS_COMMENTS_HIDDEN: boolean;
		ENABLE_SERVER_DATE: boolean;
		USERCENTRICS_DRAFT_MODE: boolean;
	};
	localStorageKeys: {
		LOCAL_STORAGE_KEY_OUTDATED_BROWSER: string;
		LOCAL_STORAGE_KEY_WEATHER: string;
	};
	keys: {
		GOOGLE_MAPS_API_KEY: string;
		GOOGLE_TAG_MANAGER_ID: string | undefined;
		LIGHTGALLERY_LICENSE_KEY: string;
		USERCENTRICS_ID: string;
	};
	images: {
		DEFAULT_RUNDSCHAU_IMAGE: string;
		FALLBACK_IMAGE: string;
	};
	constants: {
		BRANCH: string;
		BASE_URL: string;
		appUrl: string;
		SELF_ORIGIN: string;
	};
}

const EnvironmentContext = createContext<undefined | Environment>(undefined);
EnvironmentContext.displayName = 'EnvironmentContext';

/**
 * EnvironmentProvider component that provides the specified environment to its children.
 *
 * @param children - The children components to render.
 * @param environment - The environment object to provide.
 * @return The JSX element representing the EnvironmentProvider.
 */
export function EnvironmentProvider({ children, environment }: PropsWithChildren<{ environment: Environment }>) {
	return <EnvironmentContext value={environment}>{children}</EnvironmentContext>;
}

/**
 * A custom React Hook that provides access to the environment context.
 *
 * @return The environment object from the context.
 */
export function useEnvironment(): Environment {
	const environment = use(EnvironmentContext);
	if (!environment) {
		throw new Error('You must initialize the environment before using it');
	}
	return environment;
}
